import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Quill from 'quill';
import { MultiSelect } from "react-multi-select-component";
import 'quill/dist/quill.snow.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CreateHome = () => {
  const [homeData, setHomeData] = useState({
    propertyName: '',
    streetAddress: '',
    area: '',
    lat: '',
    long: '',
    bedrooms: '',
    guestRooms: '',
    propertyUploadType: '',
    images: '',
    bathrooms: '',
    garage: '',
    about: '',
    price: '',
    access: [],
    environment: [],
    outside: [],
    sdaCategory: '',
    isRegisteredSda: false,
    stayDuration: [],
    sharedLiving: [],
    building: '',
    youtubeUrl: '',
    state: '', // Add state field
  });

  const [uploadedFiles, setUploadedFiles] = useState({
    floorPlan: null,
    propertyFlyer: null
  });

  const accessOptions = [
    'Wheelchair', 'Ramps', 'Elevator', 'Wide Doorways', 
    'Handrails', 'Roll-in Shower', 'Height-Adjustable'
  ];

  const environmentOptions = [
    'Quiet', 'Busy', 'Suburban', 'Urban', 
    'Rural', 'Close to Transport', 'Close to Shops'
  ];

  const outsideOptions = [
    'Garden', 'Patio', 'Balcony', 'Pool', 
    'BBQ Area', 'Secure Yard', 'Parking'
  ];

  const sdaCategoryOptions = [
    'Basic', 'Improved Liveability', 'Fully Accessible',
    'Robust', 'High Physical Support'
  ];

  const PropertyType = [
    'Vacancy', 'Open House'
  ];

  const stayDurationOptions = [
    'Short Term', 'Medium Term', 'Long Term', 'Permanent'
  ];

  const stateOptions = [
    'Victoria',
    'Queensland',
    'South Australia'
  ];

  const [uploadedImages, setUploadedImages] = useState([]);
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const quillRef = useRef(null);

  // Handler for file change
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setUploadedImages(prevImages => [...prevImages, ...files]);
  };

  const handleImageDelete = (indexToDelete) => {
    setUploadedImages(uploadedImages.filter((_, index) => index !== indexToDelete));
  };

  const handlePdfChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFiles(prev => ({
        ...prev,
        [fileType]: file
      }));
    }
  };

  const removePdf = (fileType) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fileType]: null
    }));
    // Reset the file input
    const fileInput = document.getElementById(fileType === 'floorPlan' ? 'floor-plan' : 'property-flyer');
    if (fileInput) fileInput.value = '';
  };

  useEffect(() => {
    if (editorRef.current && !quillRef.current) {
      quillRef.current = new Quill(editorRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean']
          ],
          clipboard: { matchVisual: false }
        },
        placeholder: 'Write about the home...'
      });
      quillRef.current.on('text-change', () => {
        setHomeData(prev => ({ ...prev, about: quillRef.current.root.innerHTML }));
      });
    }
  }, []);

  const handleChange = (e) => {
    setHomeData({ ...homeData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    // Add all text and array data
    Object.keys(homeData).forEach(key => {
      if (Array.isArray(homeData[key]) && key !== 'images') {
        formData.append(key, JSON.stringify(homeData[key]));
      } else if (key !== 'images') {
        formData.append(key, homeData[key]);
      }
    });

    // Add PDF files if they exist
    if (uploadedFiles.floorPlan) {
      formData.append('floorPlanPdf', uploadedFiles.floorPlan);
    }
    if (uploadedFiles.propertyFlyer) {
      formData.append('propertyFlyerPdf', uploadedFiles.propertyFlyer);
    }

    // Add images
    uploadedImages.forEach(image => {
      formData.append('images[]', image);
    });

    console.log(uploadedImages);

    console.log(formData);

    try {
      const response = await fetch(`${BASE_URL}/api/admin/createhome`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: formData
      });
      if (response.ok) {
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Error creating home:', error);
    }
  };

  const CustomTagInput = ({ tags, onTagsChange }) => {
    const [inputValue, setInputValue] = useState('');
  
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault();
        onTagsChange([...tags, inputValue.trim()]);
        setInputValue('');
      }
    };
  
    const removeTag = (indexToRemove) => {
      onTagsChange(tags.filter((_, index) => index !== indexToRemove));
    };
  
    return (
      <div className="border border-gray-300 rounded-md p-2">
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag, index) => (
            <span 
              key={index} 
              className="bg-gray-200 px-2 py-1 rounded-md flex items-center"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="ml-2 text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add shared living detail and press Enter"
          className="w-full p-2 border-none focus:outline-none"
        />
      </div>
    );
  };

  const PdfPreview = ({ file, onRemove }) => {
    if (!file) return null;
    
    return (
      <div className="flex items-center space-x-2 mt-2 bg-gray-50 p-2 rounded-md">
        <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V7.414A2 2 0 0014.586 6L11 2.414A2 2 0 009.586 2H7zm5 6a1 1 0 01-1-1V3.5L14.5 7H12z"/>
        </svg>
        <span className="flex-1 text-sm truncate">{file.name}</span>
        <button
          type="button"
          onClick={onRemove}
          className="p-1 hover:bg-gray-200 rounded-full"
        >
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-8 mb-16">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold mb-8 text-gray-800 border-b pb-4">Add New Property</h2>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <section className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-700 border-b pb-2">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Name</label>
                <input
                  type="text"
                  name="propertyName"
                  value={homeData.propertyName}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Building Name/Number</label>
                <input
                  type="text"
                  name="building"
                  value={homeData.building}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SDA Category</label>
                  <select
                    name="sdaCategory"
                    value={homeData.sdaCategory}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-md bg-white"
                  >
                    <option value="">Select SDA Category</option>
                    {sdaCategoryOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                  <select
                    name="propertyUploadType"
                    value={homeData.propertyUploadType}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-md bg-white"
                  >
                    <option value="">Select Property Type</option>
                    {PropertyType.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center pt-6">
                  <input
                    type="checkbox"
                    name="isRegisteredSda"
                    checked={homeData.isRegisteredSda}
                    onChange={e => setHomeData({...homeData, isRegisteredSda: e.target.checked})}
                    className="h-4 w-4 text-primary-yellow border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700">Registered SDA</label>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-700 border-b pb-2">Property Features</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Features</label>
                <MultiSelect
                  options={accessOptions.map(opt => ({ label: opt, value: opt }))}
                  value={homeData.access}
                  onChange={selected => setHomeData({...homeData, access: selected})}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Environment Features</label>
                <MultiSelect
                  options={environmentOptions.map(opt => ({ label: opt, value: opt }))}
                  value={homeData.environment}
                  onChange={selected => setHomeData({...homeData, environment: selected})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Outside Features</label>
                <MultiSelect
                  options={outsideOptions.map(opt => ({ label: opt, value: opt }))}
                  value={homeData.outside}
                  onChange={selected => setHomeData({...homeData, outside: selected})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stay Duration Options</label>
                <MultiSelect
                  options={stayDurationOptions.map(opt => ({ label: opt, value: opt }))}
                  value={homeData.stayDuration}
                  onChange={selected => setHomeData({...homeData, stayDuration: selected})}
                />
              </div>
            </div>
          </section>

          {/* Media Section */}
          <section className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-700 border-b pb-2">Media</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube Video URL</label>
                <input
                  type="text"
                  name="youtubeUrl"
                  value={homeData.youtubeUrl}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md"
                  placeholder="https://youtube.com/..."
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Floor Plan PDF</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md relative">
                    {!uploadedFiles.floorPlan ? (
                      <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="floor-plan" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-yellow hover:text-yellow-500">
                            <span>Upload a file</span>
                            <input
                              id="floor-plan"
                              name="floor-plan"
                              type="file"
                              className="sr-only"
                              accept=".pdf"
                              onChange={(e) => handlePdfChange(e, 'floorPlan')}
                            />
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">PDF up to 10MB</p>
                      </div>
                    ) : (
                      <PdfPreview
                        file={uploadedFiles.floorPlan}
                        onRemove={() => removePdf('floorPlan')}
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Flyer PDF</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md relative">
                    {!uploadedFiles.propertyFlyer ? (
                      <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="property-flyer" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-yellow hover:text-yellow-500">
                            <span>Upload a file</span>
                            <input
                              id="property-flyer"
                              name="property-flyer"
                              type="file"
                              className="sr-only"
                              accept=".pdf"
                              onChange={(e) => handlePdfChange(e, 'propertyFlyer')}
                            />
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">PDF up to 10MB</p>
                      </div>
                    ) : (
                      <PdfPreview
                        file={uploadedFiles.propertyFlyer}
                        onRemove={() => removePdf('propertyFlyer')}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Property Images Section */}
          <section className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-700 border-b pb-2">Property Images</h3>
            <div className="space-y-4">
              <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="image-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-yellow hover:text-yellow-500">
                      <span>Upload images</span>
                      <input id="image-upload" type="file" className="sr-only" multiple onChange={handleFileChange} accept="image/*" />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                </div>
              </div>

              {/* Preview Grid */}
              {uploadedImages.length > 0 && (
                <div className="grid grid-cols-4 gap-4">
                  {uploadedImages.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={URL.createObjectURL(img)}
                        alt={`upload-${idx}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleImageDelete(idx)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Location Details Section */}
          <section className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-700 border-b pb-2">Location Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <input
                  type="text"
                  name="streetAddress"
                  value={homeData.streetAddress}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                <input
                  type="text"
                  name="area"
                  value={homeData.area}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  name="state"
                  value={homeData.state}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md bg-white"
                >
                  <option value="">Select State</option>
                  {stateOptions.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    name="lat"
                    value={homeData.lat}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    name="long"
                    value={homeData.long}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Property Details Section */}
          <section className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-700 border-b pb-2">Property Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shared Living Details</label>
                <CustomTagInput
                  tags={homeData.sharedLiving}
                  onTagsChange={(newTags) => setHomeData({ ...homeData, sharedLiving: newTags })}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Press Enter to add each shared living detail
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                  <input
                    type="number"
                    name="bedrooms"
                    min="0"
                    value={homeData.bedrooms}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guest Rooms</label>
                  <input
                    type="number"
                    name="guestRooms"
                    min="0"
                    value={homeData.guestRooms}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                  <input
                    type="number"
                    name="bathrooms"
                    min="0"
                    step="0.5"
                    value={homeData.bathrooms}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Garage Spaces</label>
                  <input
                    type="number"
                    name="garage"
                    min="0"
                    value={homeData.garage}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Price Section */}
          <section className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-700 border-b pb-2">Pricing</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rent (%)</label>
              <input
                type="number"
                name="price"
                min="0"
                step="0.1"
                value={homeData.price}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-md"
                placeholder="Enter rent as %"
              />
            </div>
          </section>

          {/* Description Section */}
          <section className="space-y-6">            <h3 className="text-xl font-semibold text-gray-700 border-b pb-2">Description</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property Description</label>
              <div 
                ref={editorRef} 
                className="bg-white border border-gray-300 rounded-md shadow-sm"
                style={{ minHeight: '200px' }}
              />
            </div>
          </section>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-3 px-4 bg-primary-yellow text-gray-800 rounded-md hover:bg-yellow-400 transition duration-200 font-semibold mt-8"
          >
            Add Property
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateHome;
